import { FieldDescriptor } from '@nimbus-global/features/permissions/models/field-descriptor.model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { NimbusPermissionKey } from '../enums/nimbus-permission-key.enum';

export class UserGeneralTabFields {
  public static userList: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.Users,
    translationInfo: { translationKey: TranslationKeys.GeneralUserEntities },
  };

  public static displayName: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersGeneral,
    translationInfo: { translationKey: TranslationKeys.AdminUserListColumnDisplayName },
  };
  public static organizationUnit: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersGeneral,
    translationInfo: { translationKey: TranslationKeys.GeneralOrganizationUnit },
  };
  public static firstName: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersGeneral,
    translationInfo: { translationKey: TranslationKeys.AdminUserListColumnFirstName },
  };
  public static lastName: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersGeneral,
    translationInfo: { translationKey: TranslationKeys.AdminUserListColumnLastName },
  };
  public static upn: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersGeneral,
    translationInfo: { translationKey: TranslationKeys.AdminUserListColumnUpn },
  };
  public static o365Id: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersGeneral,
    translationInfo: { translationKey: TranslationKeys.AdminUserListColumnO365Id },
  };
  public static nimbusUnifyConsent: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersGeneral,
    translationInfo: { translationKey: TranslationKeys.AdminUsersNimbusUnify },
    dependingOnFF: Feature.NimbusUnifyUi,
  };

  public static userLicenses: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: {
      translationKey: TranslationKeys.AdminLicenses,
      optionsTranslationInfo: {
        translationKeys: [
          TranslationKeys.AdminLicensesContactCenter,
          TranslationKeys.AdminLicensesWebInteract,
          TranslationKeys.AdminLicensesAttendantConsole,
          TranslationKeys.AdminLicensesAssistant,
          TranslationKeys.AdminLicensingCompanion,
          TranslationKeys.AdminLicensingDirectCallHandling,
        ],
      },
    },
  };

  public static contactCenter: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensesContactCenter },
    parent: UserGeneralTabFields.userLicenses,
  };
  public static interact: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensesWebInteract },
    parent: UserGeneralTabFields.userLicenses,
    dependingOnFF: Feature.Interact,
  };
  public static attendantConsole: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensesAttendantConsole },
    parent: UserGeneralTabFields.userLicenses,
  };
  public static assistant: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensesAssistant },
    parent: UserGeneralTabFields.userLicenses,
  };
  public static companion: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensingCompanion },
    parent: UserGeneralTabFields.userLicenses,
    dependingOnFF: Feature.Companion,
  };
  public static directCallHandling: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensingDirectCallHandling },
    parent: UserGeneralTabFields.userLicenses,
    dependingOnFF: Feature.DirectCallHandling,
  };

  public static userModalities: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: {
      translationKey: TranslationKeys.AdminUserDetailsModalities,
      optionsTranslationInfo: {
        translationKeys: [
          TranslationKeys.AdminTaskParallelization,
          TranslationKeys.AdminTPTotalTaskLimit,
          TranslationKeys.AdminLicensingAudioVideo,
          TranslationKeys.AdminTPAVTaskLimit,
          TranslationKeys.AdminLicensingInstantMessaging,
          TranslationKeys.AdminTPIMTaskLimit,
          TranslationKeys.AdminLicensingExternalTask,
          TranslationKeys.AdminTPETTaskLimit,
          TranslationKeys.AdminLicensingEmail,
          TranslationKeys.AdminTPEmailTaskLimit,
          TranslationKeys.AdminTPAllowTaskActivationWhileInActiveTask,
        ],
      },
    },
  };

  public static taskParallelization: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminTaskParallelization },
    parent: UserGeneralTabFields.userModalities,
  };
  public static totalTaskLimit: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminTPTotalTaskLimit },
    parent: UserGeneralTabFields.userModalities,
  };
  public static audioVideo: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensingAudioVideo },
    parent: UserGeneralTabFields.userModalities,
  };
  public static modalityTaskLimit: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminTPModalityTaskLimit },
    parent: UserGeneralTabFields.userModalities,
  };
  public static instantMessaging: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensingInstantMessaging },
    parent: UserGeneralTabFields.userModalities,
    dependingOnFF: Feature.InstantMessaging,
  };
  public static imTaskLimit: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminTPIMTaskLimit },
    parent: UserGeneralTabFields.userModalities,
    dependingOnFF: Feature.InstantMessaging,
  };
  public static externalTask: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensingExternalTask },
    parent: UserGeneralTabFields.userModalities,
  };
  public static email: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminLicensingEmail },
    parent: UserGeneralTabFields.userModalities,
  };
  public static allowTaskActivationWhileInActiveTask: FieldDescriptor = {
    permissionKey: NimbusPermissionKey.UsersLicencesAndModalities,
    translationInfo: { translationKey: TranslationKeys.AdminTPAllowTaskActivationWhileInActiveTask },
    parent: UserGeneralTabFields.userModalities,
    dependingOnFF: Feature.AllowTaskActivationWhileInActiveTask,
  };
}
